import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Mail,
  Phone,
  Calendar,
  Building2,
  MapPin,
  Briefcase,
  CreditCard,
  FileText,
  Award,
  Laptop,
  ArrowLeft,
  Edit3,
  Plus,
  Trash2,
  CheckCircle2,
  Loader2,
  Shield,
  Save,
  Upload,
  Download,
  Eye,
  Clock,
  ExternalLink,
  X,
  FileCheck,
  FileUp,
  Fingerprint,
} from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@jira-clone/ui'
import toast from 'react-hot-toast'
import { api } from '../api/client'
import { getApiErrorMessage } from '../shared/utils/apiError'
import { resolveFileUrl } from '../shared/utils/fileUrl'
import { useAuth } from '../context/AuthContext'
import { ROLES, isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { useDepartments } from '../shared/hooks/useDepartments'
import { STORABLE_ROLES } from '../shared/hooks/usePermissionTemplates'
import { cn, cardClassName } from '@jira-clone/ui'

// Friendly display labels for the same storable role enum Create User uses
// (STORABLE_ROLES) — the value stored/sent is always the raw enum.
const ROLE_LABELS: Record<(typeof STORABLE_ROLES)[number], string> = {
  OWNER: 'Superadmin',
  ADMIN: 'Admin',
  MEMBER: 'Employee',
  VIEWER: 'Viewer',
}

type TabType =
  | 'basic'
  | 'employment'
  | 'contact'
  | 'banking'
  | 'certifications'
  | 'documents'
  | 'activity'
  | 'assets'

// Default 7 Employee Certifications matching screenshot 1
const DEFAULT_CERTIFICATIONS = [
  { id: 'cert-offer', name: 'Offer Letter', category: 'System', status: 'Not generated' },
  { id: 'cert-appointment', name: 'Appointment Letter', category: 'System', status: 'Not generated' },
  { id: 'cert-enrolment', name: 'Enrolment Form', category: 'System', status: 'Not generated' },
  { id: 'cert-induction', name: 'Induction Acknowledgement', category: 'System', status: 'Not generated' },
  { id: 'cert-nda', name: 'Non-Disclosure Agreement (NDA)', category: 'System', status: 'Not generated' },
  { id: 'cert-code-of-conduct', name: 'Code of Conduct', category: 'System', status: 'Not generated' },
  { id: 'cert-relieving', name: 'Relieving Letter', category: 'System', status: 'Not generated' },
]

// Default 15 Documents matching screenshot 2
const DEFAULT_DOCUMENTS = [
  { id: 'doc-aadhar', name: 'Aadhar Card (with full DOB)', expiryDate: 'No expiry date', status: 'Pending', category: 'Identity' },
  { id: 'doc-pan', name: 'PAN Card', expiryDate: 'No expiry date', status: 'Pending', category: 'Identity' },
  { id: 'doc-leaving', name: 'School Leaving Certificate', expiryDate: 'No expiry date', status: 'Pending', category: 'Education' },
  { id: 'doc-electricity', name: 'Electricity Bill (Address Proof)', expiryDate: 'No expiry date', status: 'Pending', category: 'Address' },
  { id: 'doc-photos', name: '2 Passport Size Photos', expiryDate: 'No expiry date', status: 'Pending', category: 'Personal' },
  { id: 'doc-pf-esic', name: 'PF & ESIC Details', expiryDate: 'No expiry date', status: 'Pending', category: 'Financial' },
  { id: 'doc-10th', name: '10th Marksheet', expiryDate: 'No expiry date', status: 'Pending', category: 'Education' },
  { id: 'doc-12th', name: '12th Marksheet', expiryDate: 'No expiry date', status: 'Pending', category: 'Education' },
  { id: 'doc-bachelor', name: "Bachelor's Marksheet", expiryDate: 'No expiry date', status: 'Pending', category: 'Education' },
  { id: 'doc-master', name: "Master's Marksheet (if applicable)", expiryDate: 'No expiry date', status: 'Pending', category: 'Education' },
  { id: 'doc-salary', name: 'Previous Salary Slip (last 6 months)', expiryDate: 'No expiry date', status: 'Pending', category: 'Experience' },
  { id: 'doc-offer-prev', name: 'Previous Org Offer & Appointment Letter', expiryDate: 'No expiry date', status: 'Pending', category: 'Experience' },
  { id: 'doc-relieving-prev', name: 'Previous Org Relieving Letter', expiryDate: 'No expiry date', status: 'Pending', category: 'Experience' },
  { id: 'doc-police', name: 'Police Clearance Certificate', expiryDate: 'No expiry date', status: 'Pending', category: 'Verification' },
  { id: 'doc-dl', name: 'Driving License (if applicable)', expiryDate: 'No expiry date', status: 'Pending', category: 'Identity' },
]

export default function EmployeeProfilePage() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()

  const [activeTab, setActiveTab] = useState<TabType>('basic')
  const [loading, setLoading] = useState(true)
  const [profileData, setProfileData] = useState<any>(null)
  const [userData, setUserData] = useState<any>(null)
  // Single source of truth for department options — same DB-backed hook
  // Create User / Manage Access use, replacing a separately-fetched copy
  // that fell back to a hardcoded list when empty (which could offer a
  // department name the backend would then reject).
  const { data: departmentOptions = [] } = useDepartments()
  const [canManageAccess, setCanManageAccess] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editTab, setEditTab] = useState<'basic' | 'employment' | 'contact' | 'banking' | 'records'>('basic')

  // File Upload State
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadTarget, setUploadTarget] = useState<{ id: string; name: string; type: 'cert' | 'doc' } | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  // Document Preview Modal
  const [previewModal, setPreviewModal] = useState<{ open: boolean; title: string; fileUrl: string; fileName?: string } | null>(null)

  // Custom Certification Modal
  const [customCertModal, setCustomCertModal] = useState(false)
  const [customCertForm, setCustomCertForm] = useState({
    name: '',
    issuer: '',
    issueDate: '',
    expiryDate: '',
    credentialId: '',
    credentialUrl: '',
  })

  // Custom Document Modal
  const [customDocModal, setCustomDocModal] = useState(false)
  const [customDocForm, setCustomDocForm] = useState({
    name: '',
    category: 'General',
    expiryDate: 'No expiry date',
  })

  // Edit form state
  const [form, setForm] = useState<any>({
    fullName: '',
    employeeId: '',
    employeeCode: '',
    email: '',
    phoneNumber: '',
    dob: '',
    gender: '',
    maritalStatus: '',
    bloodGroup: '',
    disability: 'None',

    designation: '',
    department: '',
    dateOfJoining: '',
    location: '',
    employmentType: 'Full-time',
    reportingTo: '',
    highestQualification: '',
    skills: '',

    phone: '',
    alternatePhone: '',
    currentAddress: '',
    permanentAddress: '',
    aadharNumber: '',
    panNumber: '',
    uanNumber: '',
    esicNumber: '',
    drivingLicense: '',

    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branch: '',

    authority: 'MEMBER' as (typeof STORABLE_ROLES)[number],

    workExperience: [] as any[],
    education: [] as any[],
    familyMembers: [] as any[],
    certifications: [] as any[],
    assignedAssets: [] as any[],
  })

  // Quick Access Management dropdown state — the same storable
  // OWNER/ADMIN/MEMBER/VIEWER enum Create User uses, not a display label.
  const [authorityVal, setAuthorityVal] = useState<(typeof STORABLE_ROLES)[number]>('MEMBER')
  const [departmentVal, setDepartmentVal] = useState('')
  const [updatingAccess, setUpdatingAccess] = useState(false)

  const isSuperadmin = isSuperAdminOrOwner(currentUser?.role)
  const targetId = id || 'me'
  const isSelf = !id || id === 'me' || id === currentUser?.id

  const loadProfile = async () => {
    setLoading(true)
    try {
      const endpoint = targetId === 'me' ? '/profile/me' : `/users/${targetId}/profile`
      const res = await api.get<any>(endpoint)
      const data = res.data !== undefined ? res.data : res
      const profile = data.profile || {}
      const user = data.user || {}
      setProfileData(profile)
      setUserData(user)
      setCanManageAccess(!!data.canManageAccess || isSuperadmin)

      // The profile endpoint returns SUPERADMIN as OWNER's display alias
      // (see handleGetProfile on the backend) — map it back to the stored
      // enum value the dropdown/API actually use. VIEWER passes through
      // unchanged; anything else (including a genuinely missing role)
      // defaults to MEMBER, same default GoodDayOrganizationMember uses.
      const roleStr: (typeof STORABLE_ROLES)[number] =
        user.role === ROLES.SUPERADMIN || user.role === ROLES.OWNER
          ? 'OWNER'
          : user.role === ROLES.ADMIN
          ? 'ADMIN'
          : user.role === ROLES.VIEWER
          ? 'VIEWER'
          : 'MEMBER'

      setAuthorityVal(roleStr)
      setDepartmentVal(user.department || profile.department || 'General')

      setForm({
        fullName: profile.fullName || user.name || '',
        employeeId: profile.employeeId || user.employeeId || '',
        employeeCode: profile.employeeCode || profile.employeeId || user.employeeId || '',
        email: profile.email || user.email || '',
        phoneNumber: profile.phoneNumber || '',
        dob: profile.dob || '',
        gender: profile.gender || '',
        maritalStatus: profile.maritalStatus || '',
        bloodGroup: profile.bloodGroup || '',
        disability: profile.disability || 'None',

        designation: profile.designation || user.departmentRole || '',
        department: profile.department || user.department || 'General',
        dateOfJoining: profile.dateOfJoining || (user.joinedAt ? String(user.joinedAt).split('T')[0] : ''),
        location: profile.location || '',
        employmentType: profile.employmentType || 'Full-time',
        reportingTo: profile.reportingTo || '',
        highestQualification: profile.highestQualification || '',
        skills: Array.isArray(profile.skills) ? profile.skills.join(', ') : profile.skills || '',

        phone: profile.phoneNumber || '',
        alternatePhone: profile.alternatePhone || '',
        currentAddress: profile.currentAddress || '',
        permanentAddress: profile.permanentAddress || '',
        aadharNumber: profile.identityDocuments?.aadharNumber || '',
        panNumber: profile.identityDocuments?.panNumber || '',
        uanNumber: profile.identityDocuments?.uanNumber || '',
        esicNumber: profile.identityDocuments?.esicNumber || '',
        drivingLicense: profile.identityDocuments?.drivingLicense || '',

        bankName: profile.bankDetails?.bankName || '',
        accountNumber: profile.bankDetails?.accountNumber || '',
        ifscCode: profile.bankDetails?.ifscCode || '',
        branch: profile.bankDetails?.branch || '',

        authority: roleStr,

        workExperience: profile.workExperience || [],
        education: profile.education || [],
        familyMembers: profile.familyMembers || [],
        certifications: profile.certifications || [],
        assignedAssets: profile.assignedAssets || [],
      })
    } catch (err: any) {
      console.error('Failed to load profile:', err)
      toast.error('Could not load employee profile')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProfile()
  }, [targetId])

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      const payload: any = {
        fullName: form.fullName,
        employeeId: form.employeeId,
        email: form.email,
        phoneNumber: form.phoneNumber || form.phone,
        dob: form.dob,
        gender: form.gender,
        maritalStatus: form.maritalStatus,
        bloodGroup: form.bloodGroup,
        disability: form.disability || 'None',

        designation: form.designation,
        department: form.department,
        dateOfJoining: form.dateOfJoining,
        location: form.location,
        employmentType: form.employmentType,
        reportingTo: form.reportingTo,
        highestQualification: form.highestQualification,
        skills: form.skills ? form.skills.split(',').map((s: string) => s.trim()).filter(Boolean) : [],

        alternatePhone: form.alternatePhone,
        currentAddress: form.currentAddress,
        permanentAddress: form.permanentAddress,
        identityDocuments: {
          aadharNumber: form.aadharNumber,
          panNumber: form.panNumber,
          uanNumber: form.uanNumber,
          esicNumber: form.esicNumber,
          drivingLicense: form.drivingLicense,
        },

        bankDetails: {
          bankName: form.bankName,
          accountNumber: form.accountNumber,
          ifscCode: form.ifscCode,
          branch: form.branch,
        },

        workExperience: form.workExperience,
        education: form.education,
        familyMembers: form.familyMembers,
        certifications: form.certifications,
        assignedAssets: form.assignedAssets,
      }

      if (canManageAccess) {
        payload.authority = form.authority
      }

      const endpoint = `/users/${userData?.id || targetId}/profile`
      await api.put(endpoint, payload, { skipErrorToast: true })
      toast.success('Profile saved successfully!')
      setIsEditModalOpen(false)
      loadProfile()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'update this profile'))
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateAccess = async (newAuth?: string, newDept?: string) => {
    if (!canManageAccess || !userData?.id) return
    setUpdatingAccess(true)
    try {
      const payload: any = {}
      if (newAuth !== undefined) payload.authority = newAuth
      if (newDept !== undefined) payload.department = newDept

      await api.put(`/users/${userData.id}/profile`, payload, { skipErrorToast: true })
      toast.success('Access settings updated!')
      loadProfile()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'update access settings'))
    } finally {
      setUpdatingAccess(false)
    }
  }

  // File Upload Trigger
  const triggerUpload = (id: string, name: string, type: 'cert' | 'doc') => {
    setUploadTarget({ id, name, type })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  // File Upload Handler
  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !uploadTarget) return
    setIsUploading(true)
    const toastId = toast.loading(`Uploading ${file.name}...`)

    try {
      let fileUrl = ''
      // Attempt upload to /upload endpoint
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post<any>('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          skipErrorToast: true,
        })
        if (res?.data?.url || res?.url) {
          fileUrl = res.data?.url || res.url
        }
      } catch {
        // Fallback to data URL
      }

      if (!fileUrl) {
        fileUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(file)
        })
      }

      const { id: itemId, name: itemName, type } = uploadTarget

      if (type === 'cert') {
        const currentCerts: any[] = getEffectiveCertifications()
        const index = currentCerts.findIndex((c: any) => c.id === itemId || c.name.toLowerCase() === itemName.toLowerCase())
        const updatedItem = {
          id: itemId,
          name: itemName,
          category: 'System',
          status: 'Uploaded',
          fileUrl,
          fileName: file.name,
          uploadedAt: new Date().toLocaleDateString(),
        }
        if (index >= 0) {
          currentCerts[index] = { ...currentCerts[index], ...updatedItem }
        } else {
          currentCerts.push(updatedItem)
        }
        await api.put(`/users/${userData?.id || targetId}/profile`, { certifications: currentCerts }, { skipErrorToast: true })
        toast.success(`${itemName} uploaded successfully!`, { id: toastId })
      } else {
        const currentDocs: any[] = getEffectiveDocuments()
        const index = currentDocs.findIndex((d: any) => d.id === itemId || d.name.toLowerCase() === itemName.toLowerCase())
        const updatedItem = {
          id: itemId,
          name: itemName,
          category: 'General',
          status: 'Verified',
          fileUrl,
          fileName: file.name,
          fileSize: `${(file.size / 1024).toFixed(1)} KB`,
          uploadedAt: new Date().toLocaleDateString(),
        }
        if (index >= 0) {
          currentDocs[index] = { ...currentDocs[index], ...updatedItem }
        } else {
          currentDocs.push(updatedItem)
        }
        await api.put(`/users/${userData?.id || targetId}/profile`, { documents: currentDocs }, { skipErrorToast: true })
        toast.success(`${itemName} uploaded successfully!`, { id: toastId })
      }

      loadProfile()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'upload this document'), { id: toastId })
    } finally {
      setIsUploading(false)
      setUploadTarget(null)
    }
  }

  // Download Handler
  const handleDownload = (fileUrl?: string, fileName?: string) => {
    if (!fileUrl) {
      toast.error('No file available to download. Please upload a document first.')
      return
    }
    const link = document.createElement('a')
    link.href = resolveFileUrl(fileUrl)
    link.download = fileName || 'document'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('Download started')
  }

  // View / Preview Handler
  const handleView = (title: string, fileUrl?: string, fileName?: string) => {
    if (!fileUrl) {
      toast.error('No document uploaded yet. Please click Upload first.')
      return
    }
    // /upload (apps/api/src/routes/salesdocs.ts) returns a path relative to
    // the API server ("/uploads/...") - resolved to an absolute URL here so
    // it renders correctly regardless of what origin the frontend is served
    // from.
    setPreviewModal({ open: true, title, fileUrl: resolveFileUrl(fileUrl), fileName })
  }

  // Add Custom Certification Handler
  const handleAddCustomCert = async () => {
    if (!customCertForm.name.trim()) {
      toast.error('Certification name is required')
      return
    }
    try {
      const newCert = {
        id: `cert-custom-${Date.now()}`,
        name: customCertForm.name.trim(),
        issuer: customCertForm.issuer.trim(),
        issueDate: customCertForm.issueDate,
        expiryDate: customCertForm.expiryDate,
        credentialId: customCertForm.credentialId.trim(),
        credentialUrl: customCertForm.credentialUrl.trim(),
        category: 'Custom',
        status: 'Generated',
        uploadedAt: new Date().toLocaleDateString(),
      }
      const currentCerts = [...getEffectiveCertifications(), newCert]
      await api.put(`/users/${userData?.id || targetId}/profile`, { certifications: currentCerts }, { skipErrorToast: true })
      toast.success('Custom certification added!')
      setCustomCertModal(false)
      setCustomCertForm({
        name: '',
        issuer: '',
        issueDate: '',
        expiryDate: '',
        credentialId: '',
        credentialUrl: '',
      })
      loadProfile()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'add certifications'))
    }
  }

  // Add Custom Document Handler
  const handleAddCustomDoc = async () => {
    if (!customDocForm.name.trim()) {
      toast.error('Document title is required')
      return
    }
    try {
      const newDoc = {
        id: `doc-custom-${Date.now()}`,
        name: customDocForm.name.trim(),
        category: customDocForm.category || 'General',
        expiryDate: customDocForm.expiryDate || 'No expiry date',
        status: 'Pending',
        uploadedAt: new Date().toLocaleDateString(),
      }
      const currentDocs = [...getEffectiveDocuments(), newDoc]
      await api.put(`/users/${userData?.id || targetId}/profile`, { documents: currentDocs }, { skipErrorToast: true })
      toast.success('Custom document added!')
      setCustomDocModal(false)
      setCustomDocForm({
        name: '',
        category: 'General',
        expiryDate: 'No expiry date',
      })
      loadProfile()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'add documents'))
    }
  }

  const renderField = (label: string, value?: string | null) => (
    <div className="space-y-1">
      <p className="text-[12px] font-semibold text-muted-foreground/80 tracking-wide">{label}</p>
      <p className="text-sm font-semibold text-foreground break-words">
        {value && value.trim() ? value : '—'}
      </p>
    </div>
  )

  const getInitials = (name?: string) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  if (loading) {
    return (
      <div className="flex h-[75vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground font-medium">Loading profile...</p>
        </div>
      </div>
    )
  }

  const p = profileData || {}
  const u = userData || {}

  // Merge default certifications with saved certifications
  function getEffectiveCertifications() {
    const saved = p.certifications || []
    const standard = DEFAULT_CERTIFICATIONS.map((def) => {
      const match = saved.find((c: any) => c.id === def.id || c.name?.toLowerCase() === def.name.toLowerCase())
      return match ? { ...def, ...match } : def
    })
    const custom = saved.filter(
      (c: any) => !DEFAULT_CERTIFICATIONS.some((def) => def.id === c.id || def.name.toLowerCase() === c.name?.toLowerCase())
    )
    return [...standard, ...custom]
  }

  // Merge default documents with saved documents
  function getEffectiveDocuments() {
    const saved = p.documents || []
    const standard = DEFAULT_DOCUMENTS.map((def) => {
      const match = saved.find((d: any) => d.id === def.id || d.name?.toLowerCase() === def.name.toLowerCase())
      return match ? { ...def, ...match } : def
    })
    const custom = saved.filter(
      (d: any) => !DEFAULT_DOCUMENTS.some((def) => def.id === d.id || def.name.toLowerCase() === d.name?.toLowerCase())
    )
    return [...standard, ...custom]
  }

  const effectiveCertifications = getEffectiveCertifications()
  const effectiveDocuments = getEffectiveDocuments()

  const tabList: Array<{ id: TabType; label: string }> = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'employment', label: 'Employment' },
    { id: 'contact', label: 'Contact' },
    { id: 'banking', label: 'Banking' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'documents', label: 'Documents' },
    { id: 'activity', label: 'Activity' },
    { id: 'assets', label: 'Assigned Assets' },
  ]

  const quickInfoItems = [
    {
      label: 'Employee ID',
      value: p.employeeId || u.employeeId || '—',
      icon: CreditCard,
      bg: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    },
    {
      label: 'Employee Code',
      value: p.employeeCode || '—',
      icon: Fingerprint,
      bg: 'bg-teal-500/10 text-teal-600 dark:text-teal-400',
    },
    {
      label: 'Email',
      value: p.email || u.email || '—',
      icon: Mail,
      bg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    },
    {
      label: 'Phone',
      value: p.phoneNumber || p.phone || '—',
      icon: Phone,
      bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'DOB',
      value: p.dob || '—',
      icon: Calendar,
      bg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    },
    {
      label: 'Joined',
      value: p.dateOfJoining || (u.joinedAt ? String(u.joinedAt).split('T')[0] : '—'),
      icon: Calendar,
      bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    },
    {
      label: 'Department',
      value: p.department || u.department || '—',
      icon: Building2,
      bg: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    },
    {
      label: 'Location',
      value: p.location || '—',
      icon: MapPin,
      bg: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    },
    {
      label: 'Type',
      value: p.employmentType || 'Full-time',
      icon: Briefcase,
      bg: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    },
  ]

  return (
    <div className="w-full space-y-4">
      {/* Hidden File Input for Document / Certificate Upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileSelected}
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.webp"
      />

      {/* ======================================================== */}
      {/* TOP PROFILE HERO CARD (FIXED AT TOP, FULL WIDTH)         */}
      {/* ======================================================== */}
      <Card className="overflow-hidden">
        {/* Subtle decorative top gradient bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-primary via-indigo-500 to-purple-500" />

        <CardContent className="p-5 sm:p-6 space-y-5">
          {/* Top Row: User Avatar, Name, Badges, & Action Buttons */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {/* Avatar */}
              <div className="relative shrink-0">
                <div className="h-16 w-16 sm:h-18 sm:w-18 rounded-2xl bg-gradient-to-tr from-indigo-500/20 via-primary/20 to-purple-500/20 border-2 border-primary/30 flex items-center justify-center text-primary shadow-xs overflow-hidden">
                  {u.avatarUrl ? (
                    <img
                      src={resolveFileUrl(u.avatarUrl)}
                      alt={u.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-xl sm:text-2xl font-black tracking-wide text-primary">
                      {getInitials(u.name || p.fullName)}
                    </span>
                  )}
                </div>
              </div>

              {/* Identity & Badges */}
              <div className="space-y-1 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground truncate">
                    {u.name || p.fullName || 'Demo'}
                  </h1>

                  <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-[11px] font-bold px-2.5 py-0.5 rounded-full tracking-wider uppercase">
                    <CheckCircle2 className="h-3 w-3 mr-1 inline" />
                    {u.status || 'ACTIVE'}
                  </Badge>

                  <Badge className="bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                    <Shield className="h-3 w-3 mr-1 inline" />
                    {ROLE_LABELS[authorityVal]}
                  </Badge>

                  {(p.department || u.department) && (
                    <Badge variant="outline" className="text-[11px] font-semibold text-muted-foreground px-2.5 py-0.5 rounded-full">
                      <Building2 className="h-3 w-3 mr-1 inline" />
                      {p.department || u.department}
                    </Badge>
                  )}
                </div>

                <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">
                  {p.designation || u.departmentRole || 'Team Member'}
                  {p.employeeCode ? ` • ${p.employeeCode}` : p.employeeId ? ` • ID: ${p.employeeId}` : ''}
                </p>
              </div>
            </div>

            {/* Action Buttons & Access Controls */}
            <div className="flex items-center gap-2.5 shrink-0 self-start md:self-center">
              {canManageAccess && (
                <div className="hidden sm:flex items-center gap-2 mr-2 p-1 bg-muted/30 border border-border/60 rounded-xl">
                  <Select
                    value={authorityVal}
                    onValueChange={(val) => {
                      setAuthorityVal(val as (typeof STORABLE_ROLES)[number])
                      handleUpdateAccess(val, undefined)
                    }}
                    disabled={updatingAccess}
                  >
                    <SelectTrigger className="h-8 text-xs font-semibold bg-background w-[115px] border-none shadow-none">
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                      {STORABLE_ROLES.map((r) => (
                        <SelectItem key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={departmentVal}
                    onValueChange={(val) => {
                      setDepartmentVal(val)
                      handleUpdateAccess(undefined, val)
                    }}
                    disabled={updatingAccess}
                  >
                    <SelectTrigger className="h-8 text-xs font-semibold bg-background w-[125px] border-none shadow-none">
                      <SelectValue placeholder="Dept" />
                    </SelectTrigger>
                    <SelectContent>
                      {departmentOptions.map((d) => (
                        <SelectItem key={d.id} value={d.name}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditModalOpen(true)}
                className="h-8.5 px-3.5 text-xs font-semibold gap-1.5 border-border shadow-xs hover:bg-muted bg-card"
              >
                <Edit3 className="h-3.5 w-3.5 text-primary" />
                Edit Profile
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(-1)}
                className="h-8.5 px-3.5 text-xs font-semibold gap-1.5 border-border shadow-xs hover:bg-muted bg-card"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            </div>
          </div>

          {/* Bottom Row: Quick Info Horizontal Grid */}
          <div className="pt-4 border-t border-border/60 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {quickInfoItems.map((item, idx) => {
              const Icon = item.icon
              return (
                <div
                  key={idx}
                  className="p-2.5 rounded-xl border border-border/50 bg-muted/15 hover:bg-muted/30 transition-colors flex flex-col justify-between min-w-0"
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className={`p-1 rounded-md ${item.bg} shrink-0`}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/75 truncate">
                      {item.label}
                    </span>
                  </div>
                  <p
                    className="text-xs font-bold text-foreground truncate"
                    title={String(item.value)}
                  >
                    {item.value}
                  </p>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* ======================================================== */}
      {/* STICKY TABS HEADER                                       */}
      {/* ======================================================== */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border/80 overflow-x-auto scrollbar-none py-1">
        <div className="flex items-center gap-6 min-w-max px-1">
          {tabList.map((tab) => {
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 text-sm font-semibold transition-all relative whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'text-primary font-bold'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full transition-all" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* ======================================================== */}
      {/* FULL WIDTH TAB CONTENT                                   */}
      {/* ======================================================== */}
      <div className="space-y-4">
        {/* TAB 1: BASIC INFO */}
        {activeTab === 'basic' && (
          <Card >
            <CardContent className="p-6 sm:p-7 space-y-6">
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <h3 className="text-base font-bold text-foreground tracking-tight">
                  Basic Information
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditTab('basic')
                    setIsEditModalOpen(true)
                  }}
                  className="h-7 text-xs text-primary gap-1"
                >
                  <Edit3 className="h-3 w-3" /> Edit
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-8">
                {renderField('Full Name', p.fullName || u.name)}
                {renderField('Employee ID', p.employeeId || u.employeeId)}
                {renderField('Email', p.email || u.email)}
                {renderField('Phone Number', p.phoneNumber || p.phone)}
                {renderField('Employee Code', p.employeeCode)}
                {renderField('Date of Birth', p.dob)}
                {renderField('Gender', p.gender)}
                {renderField('Marital Status', p.maritalStatus)}
                {renderField('Blood Group', p.bloodGroup)}
                {renderField('Disability', p.disability || 'None')}
              </div>
            </CardContent>
          </Card>
        )}

        {/* TAB 2: EMPLOYMENT */}
        {activeTab === 'employment' && (
          <div className="space-y-4">
            <Card >
              <CardContent className="p-6 sm:p-7 space-y-6">
                <div className="flex items-center justify-between border-b border-border/60 pb-3">
                  <h3 className="text-base font-bold text-foreground tracking-tight">
                    Employment Details
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditTab('employment')
                      setIsEditModalOpen(true)
                    }}
                    className="h-7 text-xs text-primary gap-1"
                  >
                    <Edit3 className="h-3 w-3" /> Edit
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-8">
                  {renderField('Designation', p.designation || u.departmentRole)}
                  {renderField('Department', p.department || u.department)}
                  {renderField('Date of Joining', p.dateOfJoining || (u.joinedAt ? String(u.joinedAt).split('T')[0] : ''))}
                  {renderField('Location', p.location)}
                  {renderField('Employment Type', p.employmentType || 'Full-time')}
                  {renderField('Reporting To', p.reportingTo)}
                  {renderField('Highest Qualification', p.highestQualification)}
                  <div className="space-y-1 sm:col-span-2">
                    <p className="text-[12px] font-semibold text-muted-foreground/80">Skills</p>
                    {p.skills && p.skills.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {p.skills.map((skill: string, idx: number) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="text-xs bg-primary/5 text-primary border-primary/20 font-medium px-2.5 py-0.5 rounded-md"
                          >
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm font-semibold text-foreground">—</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Work Experience */}
            <Card >
              <CardContent className="p-6 sm:p-7 space-y-4">
                <div className="flex items-center justify-between border-b border-border/60 pb-3">
                  <h3 className="text-base font-bold text-foreground tracking-tight">
                    Work Experience
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditTab('records')
                      setIsEditModalOpen(true)
                    }}
                    className="h-7 text-xs text-primary gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add / Edit
                  </Button>
                </div>
                {p.workExperience && p.workExperience.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {p.workExperience.map((exp: any, i: number) => (
                      <div key={i} className="p-4 rounded-xl border border-border/70 bg-muted/20 space-y-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-sm text-foreground">{exp.designation}</h4>
                          <span className="text-xs text-muted-foreground font-mono">
                            {exp.fromDate} - {exp.toDate || 'Present'}
                          </span>
                        </div>
                        <p className="text-xs font-medium text-primary">{exp.company}</p>
                        {exp.description && (
                          <p className="text-xs text-muted-foreground pt-1">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center">
                    <p className="text-sm font-semibold text-muted-foreground/75">
                      No work experience recorded.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Education */}
            <Card >
              <CardContent className="p-6 sm:p-7 space-y-4">
                <div className="flex items-center justify-between border-b border-border/60 pb-3">
                  <h3 className="text-base font-bold text-foreground tracking-tight">
                    Education
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditTab('records')
                      setIsEditModalOpen(true)
                    }}
                    className="h-7 text-xs text-primary gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add / Edit
                  </Button>
                </div>
                {p.education && p.education.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {p.education.map((edu: any, i: number) => (
                      <div key={i} className="p-4 rounded-xl border border-border/70 bg-muted/20 space-y-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-sm text-foreground">{edu.degree}</h4>
                          <span className="text-xs text-muted-foreground font-mono">
                            {edu.startYear} - {edu.endYear}
                          </span>
                        </div>
                        <p className="text-xs font-medium text-primary">{edu.institution}</p>
                        {edu.fieldOfStudy && (
                          <p className="text-xs text-muted-foreground">{edu.fieldOfStudy}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center">
                    <p className="text-sm font-semibold text-muted-foreground/75">
                      No education records.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Family Members */}
            <Card >
              <CardContent className="p-6 sm:p-7 space-y-4">
                <div className="flex items-center justify-between border-b border-border/60 pb-3">
                  <h3 className="text-base font-bold text-foreground tracking-tight">
                    Family Members
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditTab('records')
                      setIsEditModalOpen(true)
                    }}
                    className="h-7 text-xs text-primary gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add / Edit
                  </Button>
                </div>
                {p.familyMembers && p.familyMembers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {p.familyMembers.map((fam: any, i: number) => (
                      <div key={i} className="p-4 rounded-xl border border-border/70 bg-muted/20 space-y-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-sm text-foreground">{fam.name}</h4>
                          <Badge variant="outline" className="text-[10px] font-semibold">
                            {fam.relationship}
                          </Badge>
                        </div>
                        {fam.phone && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1">
                            <Phone className="h-3 w-3" /> {fam.phone}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center">
                    <p className="text-sm font-semibold text-muted-foreground/75">
                      No family members recorded.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 3: CONTACT */}
        {activeTab === 'contact' && (
          <div className="space-y-4">
            <Card >
              <CardContent className="p-6 sm:p-7 space-y-6">
                <div className="flex items-center justify-between border-b border-border/60 pb-3">
                  <h3 className="text-base font-bold text-foreground tracking-tight">
                    Contact Information
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditTab('contact')
                      setIsEditModalOpen(true)
                    }}
                    className="h-7 text-xs text-primary gap-1"
                  >
                    <Edit3 className="h-3 w-3" /> Edit
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-8">
                  {renderField('Phone', p.phoneNumber || p.phone)}
                  {renderField('Alternate Phone', p.alternatePhone)}
                  {renderField('Email', p.email || u.email)}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Current Address */}
              <Card >
                <CardContent className="p-6 sm:p-7 space-y-3">
                  <h3 className="text-base font-bold text-foreground tracking-tight border-b border-border/60 pb-3">
                    Current Address
                  </h3>
                  <p className="text-sm font-semibold text-foreground/90 leading-relaxed">
                    {p.currentAddress && p.currentAddress.trim()
                      ? p.currentAddress
                      : 'Not yet filled.'}
                  </p>
                </CardContent>
              </Card>

              {/* Permanent Address */}
              <Card >
                <CardContent className="p-6 sm:p-7 space-y-3">
                  <h3 className="text-base font-bold text-foreground tracking-tight border-b border-border/60 pb-3">
                    Permanent Address
                  </h3>
                  <p className="text-sm font-semibold text-foreground/90 leading-relaxed">
                    {p.permanentAddress && p.permanentAddress.trim()
                      ? p.permanentAddress
                      : 'Not yet filled.'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Identity Documents */}
            <Card >
              <CardContent className="p-6 sm:p-7 space-y-6">
                <div className="flex items-center justify-between border-b border-border/60 pb-3">
                  <h3 className="text-base font-bold text-foreground tracking-tight">
                    Identity Documents
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditTab('contact')
                      setIsEditModalOpen(true)
                    }}
                    className="h-7 text-xs text-primary gap-1"
                  >
                    <Edit3 className="h-3 w-3" /> Edit
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-8">
                  {renderField('Aadhar Number', p.identityDocuments?.aadharNumber)}
                  {renderField('PAN Number', p.identityDocuments?.panNumber)}
                  {renderField('UAN Number', p.identityDocuments?.uanNumber)}
                  {renderField('ESIC Number', p.identityDocuments?.esicNumber)}
                  {renderField('Driving License', p.identityDocuments?.drivingLicense)}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 4: BANKING */}
        {activeTab === 'banking' && (
          <Card >
            <CardContent className="p-6 sm:p-7 space-y-6">
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <h3 className="text-base font-bold text-foreground tracking-tight">
                  Bank Account Details
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditTab('banking')
                    setIsEditModalOpen(true)
                  }}
                  className="h-7 text-xs text-primary gap-1"
                >
                  <Edit3 className="h-3 w-3" /> Edit
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-8">
                {renderField('Bank Name', p.bankDetails?.bankName)}
                {renderField('Account Number', p.bankDetails?.accountNumber)}
                {renderField('IFSC Code', p.bankDetails?.ifscCode)}
                {renderField('Branch', p.bankDetails?.branch)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ======================================================== */}
        {/* TAB 5: CERTIFICATIONS (MATCHING SCREENSHOT 1)           */}
        {/* ======================================================== */}
        {activeTab === 'certifications' && (
          <Card >
            <CardContent className="p-6 sm:p-7 space-y-6">
              <div className="flex items-center justify-between border-b border-border/60 pb-4">
                <div>
                  <h3 className="text-base font-bold text-foreground tracking-tight">
                    Employee Certifications
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Official employment letters, agreements, and certifications.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCustomCertModal(true)}
                  className="h-8.5 text-xs font-semibold gap-1.5 border-border shadow-xs hover:bg-muted"
                >
                  <Plus className="h-3.5 w-3.5 text-primary" /> Add Custom Certification
                </Button>
              </div>

              {/* 3-Column Responsive Grid matching screenshot */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {effectiveCertifications.map((cert: any) => {
                  const hasFile = Boolean(cert.fileUrl)
                  return (
                    <div
                      key={cert.id || cert.name}
                      className={cn(cardClassName({ variant: 'inset', interactive: true }), 'p-4 hover:border-primary/40 transition-all flex items-center justify-between gap-3 group')}
                    >
                      {/* Left: Icon & Text */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-11 w-11 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0 border border-purple-200/50 dark:border-purple-800/30">
                          {hasFile ? <FileCheck className="h-5 w-5 text-emerald-600" /> : <FileText className="h-5 w-5" />}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-bold text-sm text-foreground leading-tight truncate">
                            {cert.name}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {hasFile ? (
                              <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                Uploaded • {cert.fileName || 'Ready'}
                              </span>
                            ) : (
                              cert.status || 'Not generated'
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Right: Icon Buttons (Upload, View, Download) */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        {/* View Icon Button */}
                        {hasFile && (
                          <Button
                            variant="outline"
                            size="icon"
                            title="View Document"
                            onClick={() => handleView(cert.name, cert.fileUrl, cert.fileName)}
                            className="h-8.5 w-8.5 rounded-xl border-border/80 hover:bg-primary/10 hover:text-primary transition-colors text-muted-foreground"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}

                        {/* Download Icon Button */}
                        {hasFile && (
                          <Button
                            variant="outline"
                            size="icon"
                            title="Download Document"
                            onClick={() => handleDownload(cert.fileUrl, cert.fileName || `${cert.name}.pdf`)}
                            className="h-8.5 w-8.5 rounded-xl border-border/80 hover:bg-emerald-500/10 hover:text-emerald-600 transition-colors text-muted-foreground"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}

                        {/* Upload Icon Button */}
                        <Button
                          variant="outline"
                          size="icon"
                          title={hasFile ? 'Re-upload Document' : 'Upload Document'}
                          onClick={() => triggerUpload(cert.id, cert.name, 'cert')}
                          disabled={isUploading}
                          className={`h-8.5 w-8.5 rounded-xl border-border/80 transition-colors ${
                            hasFile
                              ? 'hover:bg-muted text-muted-foreground'
                              : 'hover:bg-primary/10 hover:text-primary text-foreground/80'
                          }`}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ======================================================== */}
        {/* TAB 6: DOCUMENTS (MATCHING SCREENSHOT 2)                */}
        {/* ======================================================== */}
        {activeTab === 'documents' && (
          <Card >
            <CardContent className="p-6 sm:p-7 space-y-6">
              <div className="flex items-center justify-between border-b border-border/60 pb-4">
                <div>
                  <h3 className="text-base font-bold text-foreground tracking-tight">
                    Documents
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Official employee identity, educational, and experience records.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCustomDocModal(true)}
                  className="h-8.5 text-xs font-semibold gap-1.5 border-border shadow-xs hover:bg-muted"
                >
                  <Plus className="h-3.5 w-3.5 text-primary" /> Add Custom Document
                </Button>
              </div>

              {/* 3-Column Responsive Grid matching screenshot 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {effectiveDocuments.map((doc: any) => {
                  const hasFile = Boolean(doc.fileUrl)
                  const isVerified = hasFile || doc.status === 'Verified'
                  return (
                    <div
                      key={doc.id || doc.name}
                      className={cn(cardClassName({ variant: 'inset', interactive: true }), 'p-4 hover:border-primary/40 transition-all flex flex-col justify-between gap-3 min-h-[118px] group')}
                    >
                      {/* Top Part: Icon + Title + Expiry + Status Badge */}
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-muted/60 text-muted-foreground flex items-center justify-center shrink-0 border border-border/50">
                          {hasFile ? <FileCheck className="h-4.5 w-4.5 text-emerald-600" /> : <FileText className="h-4.5 w-4.5" />}
                        </div>
                        <div className="space-y-1 min-w-0 flex-1">
                          <h4 className="font-bold text-sm text-foreground leading-snug break-words">
                            {doc.name}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            {doc.expiryDate || 'No expiry date'}
                          </p>

                          <div className="pt-0.5">
                            {isVerified ? (
                              <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" /> Verified
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                <Clock className="h-3 w-3" /> Pending
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Bottom Part: Action Icon Buttons */}
                      <div className="flex items-center justify-between pt-2 border-t border-border/40">
                        <span className="text-[11px] text-muted-foreground truncate max-w-[150px]">
                          {hasFile ? doc.fileName || doc.fileSize || 'Uploaded file' : 'No file uploaded'}
                        </span>

                        <div className="flex items-center gap-1.5 shrink-0">
                          {/* View Icon Button */}
                          {hasFile && (
                            <Button
                              variant="outline"
                              size="icon"
                              title="View Document"
                              onClick={() => handleView(doc.name, doc.fileUrl, doc.fileName)}
                              className="h-8 w-8 rounded-xl border-border/80 hover:bg-primary/10 hover:text-primary transition-colors text-muted-foreground"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          )}

                          {/* Download Icon Button */}
                          {hasFile && (
                            <Button
                              variant="outline"
                              size="icon"
                              title="Download Document"
                              onClick={() => handleDownload(doc.fileUrl, doc.fileName || `${doc.name}.pdf`)}
                              className="h-8 w-8 rounded-xl border-border/80 hover:bg-emerald-500/10 hover:text-emerald-600 transition-colors text-muted-foreground"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          )}

                          {/* Upload Icon Button */}
                          <Button
                            variant="outline"
                            size="icon"
                            title={hasFile ? 'Re-upload Document' : 'Upload Document'}
                            onClick={() => triggerUpload(doc.id, doc.name, 'doc')}
                            disabled={isUploading}
                            className={`h-8 w-8 rounded-xl border-border/80 transition-colors ${
                              hasFile
                                ? 'hover:bg-muted text-muted-foreground'
                                : 'hover:bg-primary/10 hover:text-primary text-foreground/80'
                            }`}
                          >
                            <Upload className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* TAB 7: ACTIVITY */}
        {activeTab === 'activity' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              'Attendance',
              'Leave',
              'Performance',
              'Compensation',
              'Payroll',
              'Assets',
              'Requests',
              'Separation',
            ].map((section) => (
              <Card key={section} >
                <CardContent className="p-5 space-y-2">
                  <h4 className="text-sm font-bold text-foreground">{section}</h4>
                  <p className="text-xs text-muted-foreground/70 italic">No records yet.</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* TAB 8: ASSIGNED ASSETS */}
        {activeTab === 'assets' && (
          <Card >
            <CardContent className="p-6 sm:p-7 space-y-4">
              <h3 className="text-base font-bold text-foreground tracking-tight border-b border-border/60 pb-3">
                Assigned Assets
              </h3>
              {p.assignedAssets && p.assignedAssets.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {p.assignedAssets.map((asset: any, i: number) => (
                    <div key={i} className="p-4 rounded-xl border border-border/70 bg-muted/20 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Laptop className="h-4 w-4 text-primary" />
                          <h4 className="font-bold text-sm text-foreground">{asset.assetName}</h4>
                        </div>
                        <Badge variant="outline" className="text-[10px] font-semibold">
                          {asset.status || 'Assigned'}
                        </Badge>
                      </div>
                      {asset.category && (
                        <p className="text-xs text-muted-foreground">Category: {asset.category}</p>
                      )}
                      {asset.assetTag && (
                        <p className="text-xs font-mono text-muted-foreground">Tag: {asset.assetTag}</p>
                      )}
                      {asset.serialNumber && (
                        <p className="text-xs font-mono text-muted-foreground">S/N: {asset.serialNumber}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <Laptop className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-muted-foreground/75">
                    No assets assigned yet.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ======================================================== */}
      {/* DOCUMENT PREVIEW MODAL                                   */}
      {/* ======================================================== */}
      {previewModal && (
        <Dialog open={previewModal.open} onOpenChange={(open) => !open && setPreviewModal(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 overflow-hidden">
            <DialogHeader className="p-4 sm:p-5 border-b border-border flex flex-row items-center justify-between">
              <DialogTitle className="text-base font-bold text-foreground flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                {previewModal.title}
              </DialogTitle>
              <div className="flex items-center gap-2 pr-6">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(previewModal.fileUrl, previewModal.fileName || `${previewModal.title}.pdf`)}
                  className="h-8 text-xs gap-1.5"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </Button>
              </div>
            </DialogHeader>

            <div className="p-6 overflow-y-auto flex-1 flex items-center justify-center bg-muted/20 min-h-[300px]">
              {previewModal.fileUrl.startsWith('data:image/') ||
              previewModal.fileUrl.endsWith('.png') ||
              previewModal.fileUrl.endsWith('.jpg') ||
              previewModal.fileUrl.endsWith('.jpeg') ||
              previewModal.fileUrl.endsWith('.webp') ? (
                <img
                  src={previewModal.fileUrl}
                  alt={previewModal.title}
                  className="max-h-[60vh] max-w-full rounded-lg object-contain shadow-xs border"
                />
              ) : (
                <div className="text-center space-y-3 p-8">
                  <FileCheck className="h-16 w-16 text-emerald-600 mx-auto" />
                  <p className="font-bold text-sm text-foreground">{previewModal.fileName || previewModal.title}</p>
                  <p className="text-xs text-muted-foreground">Document is ready for viewing and download.</p>
                  <Button
                    size="sm"
                    onClick={() => handleDownload(previewModal.fileUrl, previewModal.fileName || `${previewModal.title}.pdf`)}
                    className="text-xs gap-1.5"
                  >
                    <Download className="h-3.5 w-3.5" /> Download File
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ======================================================== */}
      {/* ADD CUSTOM CERTIFICATION MODAL                           */}
      {/* ======================================================== */}
      <Dialog open={customCertModal} onOpenChange={setCustomCertModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Award className="h-4.5 w-4.5 text-primary" />
              Add Custom Certification
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3.5 py-2">
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Certification Name *</Label>
              <Input
                placeholder="e.g. AWS Certified Solutions Architect"
                value={customCertForm.name}
                onChange={(e) => setCustomCertForm({ ...customCertForm, name: e.target.value })}
                className="h-8.5 text-xs"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold">Issuing Organization</Label>
              <Input
                placeholder="e.g. Amazon Web Services, Microsoft"
                value={customCertForm.issuer}
                onChange={(e) => setCustomCertForm({ ...customCertForm, issuer: e.target.value })}
                className="h-8.5 text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Issue Date</Label>
                <Input
                  type="date"
                  value={customCertForm.issueDate}
                  onChange={(e) => setCustomCertForm({ ...customCertForm, issueDate: e.target.value })}
                  className="h-8.5 text-xs"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold">Expiry Date</Label>
                <Input
                  type="date"
                  value={customCertForm.expiryDate}
                  onChange={(e) => setCustomCertForm({ ...customCertForm, expiryDate: e.target.value })}
                  className="h-8.5 text-xs"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold">Credential ID / Number</Label>
              <Input
                placeholder="e.g. AWS-12345678"
                value={customCertForm.credentialId}
                onChange={(e) => setCustomCertForm({ ...customCertForm, credentialId: e.target.value })}
                className="h-8.5 text-xs"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setCustomCertModal(false)} className="text-xs">
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddCustomCert} className="text-xs gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======================================================== */}
      {/* ADD CUSTOM DOCUMENT MODAL                                */}
      {/* ======================================================== */}
      <Dialog open={customDocModal} onOpenChange={setCustomDocModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <FileUp className="h-4.5 w-4.5 text-primary" />
              Add Custom Document
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3.5 py-2">
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Document Title *</Label>
              <Input
                placeholder="e.g. Passport Copy, Tax Document"
                value={customDocForm.name}
                onChange={(e) => setCustomDocForm({ ...customDocForm, name: e.target.value })}
                className="h-8.5 text-xs"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold">Category</Label>
              <Select
                value={customDocForm.category}
                onValueChange={(val) => setCustomDocForm({ ...customDocForm, category: val })}
              >
                <SelectTrigger className="h-8.5 text-xs">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Identity">Identity</SelectItem>
                  <SelectItem value="Address">Address Proof</SelectItem>
                  <SelectItem value="Education">Education</SelectItem>
                  <SelectItem value="Experience">Experience</SelectItem>
                  <SelectItem value="Financial">Financial / Tax</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold">Expiry Note or Date</Label>
              <Input
                placeholder="e.g. No expiry date or 2028-12-31"
                value={customDocForm.expiryDate}
                onChange={(e) => setCustomDocForm({ ...customDocForm, expiryDate: e.target.value })}
                className="h-8.5 text-xs"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setCustomDocModal(false)} className="text-xs">
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddCustomDoc} className="text-xs gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======================================================== */}
      {/* EDIT PROFILE MODAL                                       */}
      {/* ======================================================== */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 border-b border-border">
            <DialogTitle className="text-lg font-bold text-foreground flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-primary" />
              Edit Employee Profile
            </DialogTitle>
          </DialogHeader>

          {/* Modal Tab Navigator */}
          <div className="px-6 pt-3 border-b border-border flex items-center gap-3 overflow-x-auto">
            {(
              [
                { id: 'basic', label: 'Basic Info' },
                { id: 'employment', label: 'Employment' },
                { id: 'contact', label: 'Contact' },
                { id: 'banking', label: 'Banking' },
                { id: 'records', label: 'Experience & Education' },
              ] as const
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setEditTab(t.id)}
                className={`pb-2.5 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
                  editTab === t.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-6 overflow-y-auto flex-1 space-y-4">
            {/* EDIT: BASIC INFO */}
            {editTab === 'basic' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Full Name</Label>
                  <Input
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                    placeholder="Enter full name"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Employee ID</Label>
                  <Input
                    value={form.employeeId}
                    onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                    placeholder="EMP-001"
                    disabled={!canManageAccess && !!form.employeeId}
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Email</Label>
                  <Input
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@example.com"
                    disabled={!canManageAccess}
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Phone Number</Label>
                  <Input
                    value={form.phoneNumber}
                    onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                    placeholder="+91 98765 43210"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Employee Code</Label>
                  <div className="h-9 px-3 flex items-center rounded-md border border-input bg-muted/40 font-mono text-xs text-primary font-semibold select-none">
                    {form.employeeCode || '—'}
                  </div>
                  <p className="text-[10px] text-muted-foreground">Issued automatically from the department and role</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Date of Birth</Label>
                  <Input
                    type="date"
                    value={form.dob}
                    onChange={(e) => setForm({ ...form, dob: e.target.value })}
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Gender</Label>
                  <Select
                    value={form.gender}
                    onValueChange={(val) => setForm({ ...form, gender: val })}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Select Gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                      <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Marital Status</Label>
                  <Select
                    value={form.maritalStatus}
                    onValueChange={(val) => setForm({ ...form, maritalStatus: val })}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Blood Group</Label>
                  <Select
                    value={form.bloodGroup}
                    onValueChange={(val) => setForm({ ...form, bloodGroup: val })}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Select Blood Group" />
                    </SelectTrigger>
                    <SelectContent>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((bg) => (
                        <SelectItem key={bg} value={bg}>
                          {bg}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Disability</Label>
                  <Input
                    value={form.disability}
                    onChange={(e) => setForm({ ...form, disability: e.target.value })}
                    placeholder="None or specify"
                    className="h-9 text-xs"
                  />
                </div>
              </div>
            )}

            {/* EDIT: EMPLOYMENT */}
            {editTab === 'employment' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Designation</Label>
                  <Input
                    value={form.designation}
                    onChange={(e) => setForm({ ...form, designation: e.target.value })}
                    placeholder="e.g. Senior Frontend Engineer"
                    className={`h-9 text-xs ${!canManageAccess ? 'bg-muted/40' : ''}`}
                    disabled={!canManageAccess}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Department</Label>
                  {canManageAccess ? (
                    <Select
                      value={form.department}
                      onValueChange={(val) => setForm({ ...form, department: val })}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder="Select Department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departmentOptions.map((d) => (
                          <SelectItem key={d.id} value={d.name}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={form.department}
                      disabled
                      className="h-9 text-xs bg-muted/40"
                    />
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Date of Joining</Label>
                  <Input
                    type="date"
                    value={form.dateOfJoining}
                    onChange={(e) => setForm({ ...form, dateOfJoining: e.target.value })}
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Location</Label>
                  <Input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Pune, India / Remote"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Employment Type</Label>
                  <Select
                    value={form.employmentType}
                    onValueChange={(val) => setForm({ ...form, employmentType: val })}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Reporting To</Label>
                  <Input
                    value={form.reportingTo}
                    onChange={(e) => setForm({ ...form, reportingTo: e.target.value })}
                    placeholder="Manager Name"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Highest Qualification</Label>
                  <Input
                    value={form.highestQualification}
                    onChange={(e) => setForm({ ...form, highestQualification: e.target.value })}
                    placeholder="e.g. B.Tech / MBA"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Skills (comma separated)</Label>
                  <Input
                    value={form.skills}
                    onChange={(e) => setForm({ ...form, skills: e.target.value })}
                    placeholder="React, TypeScript, Node.js"
                    className="h-9 text-xs"
                  />
                </div>

                {canManageAccess && (
                  <div className="space-y-1.5 sm:col-span-2 pt-2 border-t border-border">
                    <Label className="text-xs font-semibold text-primary">Authority Role (Platform Role)</Label>
                    <Select
                      value={form.authority}
                      onValueChange={(val) => setForm({ ...form, authority: val as (typeof STORABLE_ROLES)[number] })}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder="Select Authority" />
                      </SelectTrigger>
                      <SelectContent>
                        {STORABLE_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* EDIT: CONTACT */}
            {editTab === 'contact' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">Phone</Label>
                    <Input
                      value={form.phoneNumber}
                      onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                      placeholder="Primary phone"
                      className="h-9 text-xs"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">Alternate Phone</Label>
                    <Input
                      value={form.alternatePhone}
                      onChange={(e) => setForm({ ...form, alternatePhone: e.target.value })}
                      placeholder="Secondary phone"
                      className="h-9 text-xs"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Current Address</Label>
                  <Input
                    value={form.currentAddress}
                    onChange={(e) => setForm({ ...form, currentAddress: e.target.value })}
                    placeholder="Enter current residential address"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Permanent Address</Label>
                  <Input
                    value={form.permanentAddress}
                    onChange={(e) => setForm({ ...form, permanentAddress: e.target.value })}
                    placeholder="Enter permanent address"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="pt-2 border-t border-border space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Identity Documents
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">Aadhar Number</Label>
                      <Input
                        value={form.aadharNumber}
                        onChange={(e) => setForm({ ...form, aadharNumber: e.target.value })}
                        placeholder="XXXX XXXX XXXX"
                        className="h-9 text-xs"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">PAN Number</Label>
                      <Input
                        value={form.panNumber}
                        onChange={(e) => setForm({ ...form, panNumber: e.target.value })}
                        placeholder="ABCDE1234F"
                        className="h-9 text-xs uppercase"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">UAN Number</Label>
                      <Input
                        value={form.uanNumber}
                        onChange={(e) => setForm({ ...form, uanNumber: e.target.value })}
                        placeholder="100XXXXXXXXX"
                        className="h-9 text-xs"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">ESIC Number</Label>
                      <Input
                        value={form.esicNumber}
                        onChange={(e) => setForm({ ...form, esicNumber: e.target.value })}
                        placeholder="ESIC Number"
                        className="h-9 text-xs"
                      />
                    </div>

                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs font-semibold">Driving License</Label>
                      <Input
                        value={form.drivingLicense}
                        onChange={(e) => setForm({ ...form, drivingLicense: e.target.value })}
                        placeholder="DL Number"
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* EDIT: BANKING */}
            {editTab === 'banking' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Bank Name</Label>
                  <Input
                    value={form.bankName}
                    onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                    placeholder="e.g. HDFC Bank, ICICI Bank"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Account Number</Label>
                  <Input
                    value={form.accountNumber}
                    onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
                    placeholder="XXXXXXXXXXXX"
                    className="h-9 text-xs"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">IFSC Code</Label>
                  <Input
                    value={form.ifscCode}
                    onChange={(e) => setForm({ ...form, ifscCode: e.target.value })}
                    placeholder="e.g. HDFC0001234"
                    className="h-9 text-xs uppercase"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Branch</Label>
                  <Input
                    value={form.branch}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    placeholder="Branch name or location"
                    className="h-9 text-xs"
                  />
                </div>
              </div>
            )}

            {/* EDIT: RECORDS (EXPERIENCE, EDUCATION, FAMILY) */}
            {editTab === 'records' && (
              <div className="space-y-6">
                {/* Work Experience Builder */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Work Experience
                    </h4>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setForm({
                          ...form,
                          workExperience: [
                            ...form.workExperience,
                            { company: '', designation: '', fromDate: '', toDate: '', description: '' },
                          ],
                        })
                      }
                      className="h-7 text-xs gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add
                    </Button>
                  </div>

                  {form.workExperience.map((exp: any, i: number) => (
                    <div key={i} className="p-3 rounded-lg border border-border bg-muted/20 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-muted-foreground">Entry #{i + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updated = [...form.workExperience]
                            updated.splice(i, 1)
                            setForm({ ...form, workExperience: updated })
                          }}
                          className="h-6 w-6 p-0 text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Company"
                          value={exp.company}
                          onChange={(e) => {
                            const updated = [...form.workExperience]
                            updated[i].company = e.target.value
                            setForm({ ...form, workExperience: updated })
                          }}
                          className="h-8 text-xs"
                        />
                        <Input
                          placeholder="Designation / Role"
                          value={exp.designation}
                          onChange={(e) => {
                            const updated = [...form.workExperience]
                            updated[i].designation = e.target.value
                            setForm({ ...form, workExperience: updated })
                          }}
                          className="h-8 text-xs"
                        />
                        <Input
                          placeholder="From (e.g. 2021)"
                          value={exp.fromDate}
                          onChange={(e) => {
                            const updated = [...form.workExperience]
                            updated[i].fromDate = e.target.value
                            setForm({ ...form, workExperience: updated })
                          }}
                          className="h-8 text-xs"
                        />
                        <Input
                          placeholder="To (e.g. 2023 or Present)"
                          value={exp.toDate}
                          onChange={(e) => {
                            const updated = [...form.workExperience]
                            updated[i].toDate = e.target.value
                            setForm({ ...form, workExperience: updated })
                          }}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Education Builder */}
                <div className="space-y-3 pt-3 border-t border-border">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Education
                    </h4>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setForm({
                          ...form,
                          education: [
                            ...form.education,
                            { institution: '', degree: '', fieldOfStudy: '', startYear: '', endYear: '' },
                          ],
                        })
                      }
                      className="h-7 text-xs gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add
                    </Button>
                  </div>

                  {form.education.map((edu: any, i: number) => (
                    <div key={i} className="p-3 rounded-lg border border-border bg-muted/20 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-muted-foreground">Record #{i + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updated = [...form.education]
                            updated.splice(i, 1)
                            setForm({ ...form, education: updated })
                          }}
                          className="h-6 w-6 p-0 text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Institution / University"
                          value={edu.institution}
                          onChange={(e) => {
                            const updated = [...form.education]
                            updated[i].institution = e.target.value
                            setForm({ ...form, education: updated })
                          }}
                          className="h-8 text-xs"
                        />
                        <Input
                          placeholder="Degree"
                          value={edu.degree}
                          onChange={(e) => {
                            const updated = [...form.education]
                            updated[i].degree = e.target.value
                            setForm({ ...form, education: updated })
                          }}
                          className="h-8 text-xs"
                        />
                        <Input
                          placeholder="Start Year"
                          value={edu.startYear}
                          onChange={(e) => {
                            const updated = [...form.education]
                            updated[i].startYear = e.target.value
                            setForm({ ...form, education: updated })
                          }}
                          className="h-8 text-xs"
                        />
                        <Input
                          placeholder="End Year"
                          value={edu.endYear}
                          onChange={(e) => {
                            const updated = [...form.education]
                            updated[i].endYear = e.target.value
                            setForm({ ...form, education: updated })
                          }}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Family Members Builder */}
                <div className="space-y-3 pt-3 border-t border-border">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Family Members
                    </h4>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setForm({
                          ...form,
                          familyMembers: [
                            ...form.familyMembers,
                            { name: '', relationship: '', phone: '' },
                          ],
                        })
                      }
                      className="h-7 text-xs gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add
                    </Button>
                  </div>

                  {form.familyMembers.map((fam: any, i: number) => (
                    <div key={i} className="p-3 rounded-lg border border-border bg-muted/20 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-muted-foreground">Member #{i + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updated = [...form.familyMembers]
                            updated.splice(i, 1)
                            setForm({ ...form, familyMembers: updated })
                          }}
                          className="h-6 w-6 p-0 text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          placeholder="Full Name"
                          value={fam.name}
                          onChange={(e) => {
                            const updated = [...form.familyMembers]
                            updated[i].name = e.target.value
                            setForm({ ...form, familyMembers: updated })
                          }}
                          className="h-8 text-xs"
                        />
                        <Input
                          placeholder="Relationship (e.g. Spouse, Father)"
                          value={fam.relationship}
                          onChange={(e) => {
                            const updated = [...form.familyMembers]
                            updated[i].relationship = e.target.value
                            setForm({ ...form, familyMembers: updated })
                          }}
                          className="h-8 text-xs"
                        />
                        <Input
                          placeholder="Phone Number"
                          value={fam.phone}
                          onChange={(e) => {
                            const updated = [...form.familyMembers]
                            updated[i].phone = e.target.value
                            setForm({ ...form, familyMembers: updated })
                          }}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="p-4 border-t border-border flex items-center justify-end gap-2 bg-muted/10">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditModalOpen(false)}
              disabled={saving}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSaveProfile}
              disabled={saving}
              className="text-xs gap-1.5"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
